import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { BarChart3, FlaskConical, PlayCircle, RotateCcw, X } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { SIM_TOOLS, SIM_FEATURE_IDS, isFeatureEnabled, setFeatureOverride } from "./flags";
import { IS_SHOTS } from "./params";
import { setSimContext, useSimContext } from "./context";
import { resetSimulation, useSimStore } from "./store";
import { getBridge } from "./bridge";
import { getFeatureFailures, useEnabledManifests } from "./registry";
import { FEATURE_LABELS, MARKETS, PERSONAS, REGIONS, SCENARIOS } from "./vocab";
import { isSimSurfaceAllowed } from "./surface";
import type { SimContextState, SimRegion } from "./types";

/**
 * Floating "Simulation" pill (bottom-left, demo mode only) that opens the
 * presenter's controls: scenario, date, region, persona, per-feature switches,
 * "Replay first login", "Reset simulation" and the sponsor's-view analytics.
 * Hidden in screenshot mode (?shots=1), on the landing page and on every
 * protected surface.
 */
export function SimPanel() {
  const [path] = useLocation();
  const [open, setOpen] = useState(false);
  if (!SIM_TOOLS || IS_SHOTS || path === "/" || !isSimSurfaceAllowed(path)) return null;
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="sim-panel"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "no-print fixed bottom-24 left-4 z-30 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white/95 px-3.5 py-2 text-sm font-semibold text-violet-700 shadow-soft backdrop-blur transition hover:shadow-glow lg:bottom-4",
          FOCUS_RING,
        )}
      >
        <FlaskConical className="h-4 w-4" aria-hidden />
        Simulation
      </button>
      {open && <SimPanelBody onClose={() => setOpen(false)} />}
    </>
  );
}

function SimPanelBody({ onClose }: { onClose: () => void }) {
  const context = useSimContext();
  const queryClient = useQueryClient();
  const [confirmReset, setConfirmReset] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Re-render as features load or flip.
  useEnabledManifests();
  useSimStore((s) => s.featureOverrides);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Context changes refetch everything so every screen re-reads the new world.
  const update = (patch: Partial<SimContextState>) => {
    setSimContext(patch);
    void queryClient.invalidateQueries();
  };
  const failures = getFeatureFailures();
  const isPh = context.market === "ph";
  const replay = getBridge("welcome.replay");

  return (
    <div
      id="sim-panel"
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-label="Simulation controls"
      className="no-print fixed bottom-40 left-4 z-30 max-h-[min(70vh,42rem)] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-3xl border border-violet-100 bg-white p-5 shadow-glow focus:outline-none lg:bottom-16"
    >
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-violet-400 to-purple-500"
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">Demo only</p>
          <h2 className="font-display text-lg font-semibold text-neutral-900">Simulation controls</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close simulation controls"
          className={cn("rounded-full p-1.5 text-neutral-500 transition hover:bg-neutral-100", FOCUS_RING)}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <PanelSection title="Market">
        <div className="flex flex-wrap gap-1.5">
          {MARKETS.map((m) => (
            <Pill
              key={m.id}
              active={context.market === m.id}
              title={m.hint}
              onClick={() => update({ market: m.id })}
            >
              {m.label}
            </Pill>
          ))}
        </div>
      </PanelSection>

      {isPh && (
        <PanelSection title="Scenario">
          <div className="flex flex-wrap gap-1.5">
            {SCENARIOS.map((s) => (
              <Pill
                key={s.id}
                active={context.scenario === s.id}
                title={s.hint}
                onClick={() => update({ scenario: s.id })}
              >
                {s.label}
              </Pill>
            ))}
          </div>
        </PanelSection>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        {isPh && (
          <label className="block text-xs font-semibold text-neutral-700">
            Region
            <select
              className={INPUT_CLASS}
              value={context.region}
              onChange={(e) => update({ region: e.target.value as SimRegion })}
            >
              {REGIONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block text-xs font-semibold text-neutral-700">
          Date
          <input
            type="date"
            className={INPUT_CLASS}
            value={context.simDate}
            onChange={(e) => {
              if (e.target.value) update({ simDate: e.target.value });
            }}
          />
        </label>
      </div>

      <PanelSection title="Persona">
        <div className="flex flex-wrap gap-1.5">
          {PERSONAS.map((p) => (
            <Pill
              key={p.id}
              active={context.persona === p.id}
              title={p.hint}
              onClick={() => update({ persona: p.id })}
            >
              {p.label}
            </Pill>
          ))}
        </div>
      </PanelSection>

      <PanelSection title="Features">
        <ul className="space-y-2">
          {SIM_FEATURE_IDS.map((id) => {
            const on = isFeatureEnabled(id);
            return (
              <li key={id} className="flex items-center justify-between gap-3">
                <span className="text-sm text-neutral-800">
                  {FEATURE_LABELS[id]}
                  {failures.has(id) && (
                    <span className="ml-1 text-xs font-medium text-rose-700">(failed to load)</span>
                  )}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={FEATURE_LABELS[id]}
                  onClick={() => {
                    setFeatureOverride(id, !on);
                    void queryClient.invalidateQueries();
                  }}
                  className={cn(
                    "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition",
                    FOCUS_RING,
                    on ? "bg-gradient-to-br from-sky-500 to-indigo-600" : "bg-neutral-200",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "inline-block h-5 w-5 rounded-full bg-white shadow-soft transition",
                      on ? "translate-x-[1.125rem]" : "translate-x-0.5",
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </PanelSection>

      <div className="mt-5 flex flex-col gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={!replay}
          onClick={() => {
            replay?.();
            onClose();
          }}
        >
          <PlayCircle className="h-4 w-4" aria-hidden />
          Replay first login
        </Button>
        {isFeatureEnabled("partners") && (
          <Button asChild variant="ghost" size="sm">
            <Link href="/sim/partner-analytics" onClick={onClose}>
              <BarChart3 className="h-4 w-4" aria-hidden />
              Sponsor&apos;s view: placement results
            </Link>
          </Button>
        )}
        <Button
          variant="destructiveOutline"
          size="sm"
          onClick={() => {
            if (!confirmReset) {
              setConfirmReset(true);
              return;
            }
            resetSimulation();
            window.location.assign(window.location.pathname);
          }}
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
          {confirmReset ? "Tap again to reset everything" : "Reset simulation"}
        </Button>
      </div>
    </div>
  );
}

function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">{title}</h3>
      {children}
    </section>
  );
}

function Pill({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={cn(
        "rounded-full px-3 py-1.5 text-xs font-semibold transition",
        FOCUS_RING,
        active
          ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-soft"
          : "border border-sky-100 bg-white text-neutral-700 hover:bg-sky-50 hover:text-sky-700",
      )}
    >
      {children}
    </button>
  );
}
